import { initializeApp, getApps } from 'firebase/app';
import { getAuth, GoogleAuthProvider, browserLocalPersistence, setPersistence } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

// Reads config from environment variables. Set these in a .env file at project root.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

export const auth = getAuth(app);

// Set persistence to local storage (more reliable than session storage)
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('Failed to set persistence:', error);
});

export const db = getFirestore(app);
export const googleProvider = new GoogleAuthProvider();

// Force account selection and ensure compatibility with iOS/Safari
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

export const storage = getStorage(app);

// Initialize Firebase Cloud Messaging
export const messaging = typeof window !== 'undefined' && 'serviceWorker' in navigator ? getMessaging(app) : null;

// Request notification permission and get FCM token
export const requestNotificationPermission = async () => {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    throw new Error('Notifications are not supported on this device or browser.');
  }
  if (!messaging) {
    throw new Error('Messaging not supported in your browser');
  }
  if (!process.env.REACT_APP_FIREBASE_VAPID_KEY) {
    throw new Error('Notification service not configured. Please contact support.');
  }
  try {
    // Request permission - this will show the browser popup if permission is 'default'
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      if (permission === 'denied') {
        throw new Error('Notifications blocked. To enable:\n\n1. Click the 🔒 lock icon in your address bar\n2. Find "Notifications"\n3. Change to "Allow"\n4. Refresh and try again');
      } else {
        throw new Error('Notification permission denied');
      }
    }
    // Permission granted, get FCM token
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
    });
    if (!token) {
      throw new Error('Failed to get notification token. Please refresh and try again.');
    }
    return token;
  } catch (err) {
    console.error('Error in requestNotificationPermission:', err);
    throw err;
  }
};

// Listen for foreground messages
if (messaging) {
  onMessage(messaging, async (payload) => {
    console.log('Message received in foreground:', payload);
    // Show notification using service worker when app is in foreground
    const title = payload.data?.title || payload.notification?.title;
    const body = payload.data?.body || payload.notification?.body;
    const icon = payload.notification?.icon || '/logo192.png';
    if (title) {
      try {
        let registration = await navigator.serviceWorker.getRegistration();
        if (!registration) {
          registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        }
        await registration.showNotification(title, {
          body,
          icon,
          badge: '/logo192.png',
          data: payload.data
        });
      } catch (err) {
        console.error('Failed to show foreground notification:', err);
      }
    }
  });
}
